import React from 'react';
import { motion } from 'framer-motion';
import { Calendar, Target, Zap, MessageCircle, Link, User, CheckSquare, HelpCircle } from 'lucide-react';
import { Product, PriorityScoringConfig } from '../types';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { calculatePriorityScore, getPriorityColor } from '../utils/priorityScore';

interface ProductCardProps {
  product: Product;
  onClick: () => void;
  onCommentClick: (e: React.MouseEvent) => void;
  id: string;
  scoringConfig?: PriorityScoringConfig;
}

const ProductCard: React.FC<ProductCardProps> = ({ product, onClick, onCommentClick, id, scoringConfig }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };
  const getEffortColor = (level: string) => {
    switch (level) {
      case 'Low': return 'text-matcha-600 bg-matcha-100';
      case 'Medium': return 'text-satsuma-600 bg-satsuma-100';
      case 'High': return 'text-sakura-600 bg-sakura-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getPotentialColor = (level: string) => {
    switch (level) {
      case 'Low': return 'text-gray-600 bg-gray-100';
      case 'Medium': return 'text-heian-600 bg-heian-100';
      case 'High': return 'text-matcha-600 bg-matcha-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getMarketCrowdingColor = (level: string) => {
    switch (level) {
      case 'No Competition': return 'text-green-600 bg-green-100';
      case 'Low Competition': return 'text-blue-600 bg-blue-100';
      case 'Medium Competition': return 'text-yellow-600 bg-yellow-100';
      case 'High Competition': return 'text-red-600 bg-red-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getMarketTypeColor = (type: string) => {
    switch (type) {
      case 'Blue Ocean': return 'text-blue-600 bg-blue-100';
      case 'Red Ocean': return 'text-red-600 bg-red-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getBusinessModelColor = (model: string) => {
    switch (model) {
      case 'SaaS': return 'text-purple-600 bg-purple-100';
      case 'API/Platform': return 'text-indigo-600 bg-indigo-100';
      case 'Marketplace': return 'text-orange-600 bg-orange-100';
      case 'Mobile App': return 'text-pink-600 bg-pink-100';
      case 'E-commerce': return 'text-teal-600 bg-teal-100';
      case 'Hardware': return 'text-gray-600 bg-gray-100';
      case 'Consulting': return 'text-brown-600 bg-brown-100';
      case 'Other': return 'text-gray-600 bg-gray-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getSupportOverheadColor = (level: string) => {
    switch (level) {
      case 'Low': return 'text-green-600 bg-green-100';
      case 'Medium': return 'text-yellow-600 bg-yellow-100';
      case 'High': return 'text-red-600 bg-red-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getIndustryAreaColor = (industry: string) => {
    // Generate a consistent color based on the industry name
    const colors = [
      'text-blue-600 bg-blue-100',
      'text-purple-600 bg-purple-100',
      'text-green-600 bg-green-100',
      'text-orange-600 bg-orange-100',
      'text-pink-600 bg-pink-100',
      'text-indigo-600 bg-indigo-100',
      'text-teal-600 bg-teal-100',
      'text-red-600 bg-red-100',
      'text-yellow-600 bg-yellow-100',
      'text-gray-600 bg-gray-100'
    ];
    const index = (industry || '').length % colors.length;
    return colors[index];
  };

  const getFeatureStatusColor = (status: string) => {
    switch (status) {
      case 'Not Started':
        return 'text-gray-600 bg-gray-100';
      case 'In Progress':
        return 'text-blue-600 bg-blue-100';
      case 'Completed':
        return 'text-green-600 bg-green-100';
      case 'Blocked':
        return 'text-red-600 bg-red-100';
      case 'On Hold':
        return 'text-yellow-600 bg-yellow-100';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  };

  const priorityScore = product.priorityScore || calculatePriorityScore(product, scoringConfig);

  return (
    <motion.div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      layout
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.9 }}
      whileHover={{ y: isDragging ? 0 : -2 }}
      className={`product-card ${isDragging ? 'opacity-50' : ''}`}
      onClick={onClick}
    >
      <div className="space-y-3">
        {/* Header */}
        <div className="flex items-start justify-between">
          <div className="flex items-center space-x-2">
            <h3 className="font-semibold text-gray-800 text-lg leading-tight">
              {product.name}
            </h3>
            <div className={`px-2 py-1 rounded-full text-xs font-bold ${getPriorityColor(priorityScore)}`}>
              {priorityScore}
            </div>
          </div>
          <div className="flex items-center space-x-1">
            <button
              onClick={onCommentClick}
              className="text-gray-400 hover:text-satsuma-500 transition-colors p-1 rounded"
              title="Add comment"
            >
              <MessageCircle size={16} />
            </button>
            {product.commentCount && product.commentCount > 0 && (
              <span className="text-xs text-gray-500 font-medium">
                {product.commentCount}
              </span>
            )}
          </div>
        </div>

        {/* Owner */}
        {product.owner && (
          <div className="flex items-center text-xs text-gray-600">
            <User size={12} className="mr-1" />
            <span className="font-medium">{product.owner}</span>
          </div>
        )}


        {/* Links */}
        {product.links && product.links.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {product.links.slice(0, 3).map((link) => (
              <a
                key={link.id}
                href={link.url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-blue-50 text-blue-700 hover:bg-blue-100 transition-colors"
                onClick={(e) => e.stopPropagation()}
              >
                <Link size={10} className="mr-1" />
                {link.title}
              </a>
            ))}
            {product.links.length > 3 && (
              <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600">
                +{product.links.length - 3}
              </span>
            )}
          </div>
        )}



        {/* AI Summary */}
        {product.aiSummary && (
          <p className="text-gray-600 text-xs leading-relaxed">
            {product.aiSummary}
          </p>
        )}

        {/* Metrics */}
        <div className="flex flex-wrap gap-1">
          <span className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium ${getEffortColor(product.effortLevel)}`}>
            <Zap size={10} className="mr-1" />
            {product.effortLevel}
          </span>
          <span className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium ${getPotentialColor(product.marketPotential)}`}>
            <Target size={10} className="mr-1" />
            {product.marketPotential}
          </span>
          <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
            {product.gtmReadyPercent}% GTM
          </span>
          {product.marketCrowding && (
            <span className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium ${getMarketCrowdingColor(product.marketCrowding)}`}>
              {product.marketCrowding}
            </span>
          )}
          {product.marketType && (
            <span className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium ${getMarketTypeColor(product.marketType)}`}>
              {product.marketType}
            </span>
          )}
          {product.businessModel && (
            <span className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium ${getBusinessModelColor(product.businessModel)}`}>
              {product.businessModel}
            </span>
          )}
          {product.supportOverhead && (
            <span className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium ${getSupportOverheadColor(product.supportOverhead)}`}>
              {product.supportOverhead} Support
            </span>
          )}
        </div>

        {/* Industry Areas - Only show first 3 */}
        {product.industryAreas && product.industryAreas.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {product.industryAreas.slice(0, 3).map((industry, index) => (
              <span
                key={index}
                className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium ${getIndustryAreaColor(industry || '')}`}
              >
                {industry}
              </span>
            ))}
            {product.industryAreas && product.industryAreas.length > 3 && (
              <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                +{product.industryAreas && product.industryAreas.length - 3}
              </span>
            )}
          </div>
        )}

        {/* Features - Only show first 2 */}
        {product.features && product.features.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {product.features.slice(0, 2).map((feature, index) => (
              <span
                key={feature.id || index}
                className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium ${getFeatureStatusColor(feature.status || 'Not Started')}`}
              >
                {feature.name}
              </span>
            ))}
            {product.features && product.features.length > 2 && (
              <span className="inline-block px-1.5 py-0.5 bg-white/15 rounded text-xs text-gray-500">
                +{product.features && product.features.length - 2}
              </span>
            )}
          </div>
        )}

        {product.taskCount != null && product.taskCount > 0 && (
          <div className="flex items-center text-xs text-gray-600 gap-1">
            <CheckSquare size={12} className="text-gray-400 shrink-0" />
            <span>
              {product.taskCount} task{product.taskCount === 1 ? '' : 's'}
            </span>
          </div>
        )}

        {product.marketFitQa && product.marketFitQa.length > 0 && (
          <div className="space-y-1.5 border-t border-gray-100 pt-2">
            <div className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide flex items-center gap-1">
              <HelpCircle size={10} className="shrink-0" />
              Market fit Q&A
            </div>
            {product.marketFitQa.slice(0, 2).map((q) => (
              <div
                key={q.id}
                className="text-xs text-gray-700 bg-gray-50/90 rounded px-2 py-1.5 border border-gray-100"
              >
                <div className="font-medium text-gray-800 line-clamp-2">Q: {q.question}</div>
                {q.answer ? (
                  <div className="text-gray-600 line-clamp-2 mt-0.5">A: {q.answer}</div>
                ) : null}
              </div>
            ))}
            {product.marketFitQa.length > 2 && (
              <span className="text-[10px] text-gray-500">+{product.marketFitQa.length - 2} more</span>
            )}
          </div>
        )}

        {/* Competitors - Only show first 1 */}
        {product.competitors && product.competitors.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {product.competitors.slice(0, 1).map((competitor, index) => (
              <span
                key={index}
                className="inline-block px-1.5 py-0.5 bg-sakura-50 rounded text-xs text-sakura-700"
              >
                {competitor}
              </span>
            ))}
            {product.competitors && product.competitors.length > 1 && (
              <span className="inline-block px-1.5 py-0-0.5 bg-sakura-50 rounded text-xs text-sakura-600">
                +{product.competitors && product.competitors.length - 1}
              </span>
            )}
          </div>
        )}

        {/* Date */}
        <div className="flex items-center text-xs text-gray-500 pt-1">
          <Calendar size={10} className="mr-1" />
          {product.createdAt ? new Date(product.createdAt).toLocaleDateString() : 'Unknown date'}
        </div>
      </div>
    </motion.div>
  );
};

export default ProductCard;
